import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["copilotAction", "data-test-id", "disabled", "hideIcon", "onClick", "size", "text", "tooltip", "tooltipPlacement", "use", "usageTracker", "usageTrackerAppLocationKey", "useBreezeStyles", "shape", "active", "disableTooltip"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { useSendCopilotAction } from 'copilot-toolkit/hooks/useSendCopilotAction';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useCallback } from 'react';
import { useOptionalTracker } from '../../hooks/useTracker';
import { CopilotBaseButton } from './internal/CopilotBaseButton';
import { CopilotBaseIconButton } from './internal/CopilotBaseIconButton';
import { jsx as _jsx } from "react/jsx-runtime";
const getIconUse = (use, useBreezeStyles) => {
  if (!useBreezeStyles) {
    if (use === 'breeze-special') {
      return 'transparent';
    } else return use;
  }
  if (use === 'transparent') {
    return 'transparent';
  }
  if (use === 'link-on-dark') {
    return 'link-on-dark';
  }
  if (use === 'link') {
    return 'link';
  }
  return 'transparent';
};
const CopilotActionButton = _ref => {
  let {
      copilotAction,
      'data-test-id': dataTestId,
      disabled,
      hideIcon = false,
      onClick,
      size = 'medium',
      text = '',
      tooltip,
      tooltipPlacement = 'top',
      use = 'tertiary-light',
      usageTracker,
      usageTrackerAppLocationKey,
      useBreezeStyles = true,
      shape = 'square',
      active = undefined,
      disableTooltip = false
    } = _ref,
    uiButtonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  /** We need this tracker optional based on this ticket https://git.hubteam.com/HubSpot/ai-components-ui-library/pull/168 */
  const {
    aiTracker
  } = useOptionalTracker(usageTracker, usageTrackerAppLocationKey);
  const sendCopilotAction = useSendCopilotAction();
  const openPromptTemplate = useSendOnCopilotChannel('OPEN_PROMPT_TEMPLATE');
  const onSubmitAction = useCallback(event => {
    if ('openPromptTemplate' in copilotAction && copilotAction.openPromptTemplate && copilotAction.openPromptTemplate.id) {
      openPromptTemplate({
        promptTemplateId: copilotAction.openPromptTemplate.id,
        prefillValues: copilotAction.openPromptTemplate.prefillValues
      });
      if (aiTracker) {
        aiTracker.track('copilotActionButtonInteraction', {
          type: 'open_prompt_template',
          prompt: copilotAction.openPromptTemplate.id,
          hasPrefillValues: Boolean(copilotAction.openPromptTemplate.prefillValues)
        });
      }
    } else if ('intent' in copilotAction) {
      sendCopilotAction({
        messageAction: copilotAction
      });
      if (aiTracker) {
        aiTracker.track('copilotActionButtonInteraction', {
          type: copilotAction.intent || ''
        });
      }
    }
    if (onClick) {
      onClick(event);
    }
  }, [aiTracker, copilotAction, onClick, openPromptTemplate, sendCopilotAction]);
  const iconUse = getIconUse(use, useBreezeStyles);
  return /*#__PURE__*/_jsx(Tooltip, {
    delay: BREEZE_TOOLTIP_DELAY,
    disabled: disabled || disableTooltip,
    title: tooltip || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiComponentsUiLibrary.components.copilotActionButton.tooltipCopilot"
    }),
    placement: tooltipPlacement,
    children: text !== '' ? /*#__PURE__*/_jsx(CopilotBaseButton, Object.assign({
      "data-test-id": dataTestId,
      disabled: disabled,
      hideIcon: hideIcon,
      onClick: onSubmitAction,
      size: size,
      use: use,
      useBreezeStyles: useBreezeStyles
    }, uiButtonProps, {
      children: text
    })) : /*#__PURE__*/_jsx(CopilotBaseIconButton, Object.assign({
      "data-test-id": dataTestId,
      disabled: disabled,
      onClick: onSubmitAction,
      size: size,
      use: iconUse,
      useBreezeStyles: useBreezeStyles,
      shape: shape,
      active: active
    }, uiButtonProps))
  });
};
export default CopilotActionButton;